import type { ProgressSummary } from '../../types/api';
import { dateFromKey, isoWeekNumber, localDateKey, shiftDate, startOfWeek, sumActivity } from '../../shared/date';

export interface PeriodTotal {
  activeSeconds: number;
  attempts: number;
}

export interface DayBar {
  label: string;
  height: number;
  today: boolean;
}

export interface WeekBar {
  label: string;
  height: number;
}

export interface ProgressModel {
  currentWeek: PeriodTotal;
  weekTrend: string;
  days: DayBar[];
  current28: PeriodTotal;
  activeComparison: string;
  chartMaximum: number;
  chartWeeks: WeekBar[];
}

export function buildProgressModel(progress: ProgressSummary, todayKey = localDateKey()): ProgressModel {
  const activity = new Map(progress.activity.map(entry => [entry.date, entry]));
  const today = dateFromKey(todayKey);
  const weekStart = startOfWeek(today);
  const weekdayIndex = (today.getDay() + 6) % 7;
  const currentWeek = sumActivity(activity, weekStart, today);
  const previousWeekStart = shiftDate(weekStart, -7);
  const previousComparable = sumActivity(activity, previousWeekStart, shiftDate(previousWeekStart, weekdayIndex));
  const weekTrend = comparison(currentWeek.activeSeconds, previousComparable.activeSeconds, 'Første uke');

  const rawDays = ['M', 'T', 'O', 'T', 'F', 'L', 'S'].map((label, index) => {
    const date = shiftDate(weekStart, index);
    return { label, date, seconds: activity.get(localDateKey(date))?.activeSeconds ?? 0 };
  });
  const dayMaximum = Math.max(60, ...rawDays.map(day => day.seconds));
  const days = rawDays.map(day => ({
    label: day.label,
    height: Math.round(day.seconds / dayMaximum * 100),
    today: localDateKey(day.date) === todayKey
  }));

  const current28 = sumActivity(activity, shiftDate(today, -27), today);
  const previous28 = sumActivity(activity, shiftDate(today, -55), shiftDate(today, -28));
  const activeComparison = previous28.activeSeconds > 0
    ? `${signedPercent(current28.activeSeconds, previous28.activeSeconds)} fra forrige 4 uker`
    : current28.activeSeconds > 0 ? 'Første registrerte periode' : 'Ingen registrert aktivitet';

  const weekly = Array.from({ length: 4 }, (_, index) => {
    const start = shiftDate(weekStart, (index - 3) * 7);
    return { start, minutes: sumActivity(activity, start, shiftDate(start, 6)).activeSeconds / 60 };
  });
  const chartMaximum = Math.ceil(Math.max(15, ...weekly.map(week => week.minutes)) / 15) * 15;
  const chartWeeks = weekly.map((week, index) => ({
    label: index === 3 ? 'DENNE UKEN' : `UKE ${isoWeekNumber(week.start)}`,
    height: Math.round(week.minutes / chartMaximum * 100)
  }));

  return { currentWeek, weekTrend, days, current28, activeComparison, chartMaximum, chartWeeks };
}

function signedPercent(current: number, previous: number): string {
  const change = Math.round((current - previous) / previous * 100);
  return `${change >= 0 ? '+' : ''}${change} %`;
}

function comparison(current: number, previous: number, firstLabel: string): string {
  if (previous > 0) return signedPercent(current, previous);
  return current > 0 ? firstLabel : '—';
}
